import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { Users, Plus, Pencil, Trash2, ShieldCheck, Eye, UserCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_LABELS = {
  system_admin: 'システム管理者',
  facility_admin: '事業所管理者',
  service_manager: 'サービス管理責任者',
  support_staff: '支援員',
  accountant: '経理担当',
  readonly: '閲覧のみ'
};

const ROLE_COLORS = {
  system_admin: 'bg-red-100 text-red-800 border-red-200',
  facility_admin: 'bg-violet-100 text-violet-800 border-violet-200',
  service_manager: 'bg-blue-100 text-blue-800 border-blue-200',
  support_staff: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  accountant: 'bg-amber-100 text-amber-800 border-amber-200',
  readonly: 'bg-gray-100 text-gray-600 border-gray-200'
};

const ASSIGNABLE_ROLES = ['facility_admin', 'service_manager', 'support_staff', 'accountant', 'readonly'];

export default function FacilityUsersPage() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'support_staff', facility_id: '' });
  const [saving, setSaving] = useState(false);

  const isSystemAdmin = currentUser?.role === 'system_admin';

  useEffect(() => {
    fetchUsers();
    if (isSystemAdmin) fetchFacilities();
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/facility-users`);
      setUsers(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchFacilities = async () => {
    try {
      const res = await axios.get(`${API}/admin/facilities`);
      setFacilities(res.data);
    } catch (e) { console.error(e); }
  };

  const openNew = () => {
    setEditingUser(null);
    setForm({ name: '', email: '', password: '', role: 'support_staff', facility_id: '' });
    setDialogOpen(true);
  };

  const openEdit = (u) => {
    setEditingUser(u);
    setForm({ name: u.name, email: u.email, password: '', role: u.role, facility_id: u.facility_id || '' });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editingUser) {
        const updateData = { name: form.name, role: form.role };
        await axios.put(`${API}/facility-users/${editingUser.id}`, updateData);
      } else {
        if (!form.password || form.password.length < 6) {
          alert('パスワードは6文字以上にしてください');
          setSaving(false);
          return;
        }
        await axios.post(`${API}/facility-users`, form);
      }
      setDialogOpen(false);
      fetchUsers();
    } catch (e) {
      alert('保存に失敗: ' + (e.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/facility-users/${id}`);
      fetchUsers();
    } catch (e) { alert('削除に失敗: ' + (e.response?.data?.detail || e.message)); }
  };

  if (loading) return <div className="flex items-center justify-center min-h-[400px]"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="facility-users-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-foreground">ユーザー管理</h1>
          <p className="text-muted-foreground mt-1">事業所内のユーザー: {users.length}名</p>
        </div>
        <Button className="btn-active" onClick={openNew} data-testid="add-user-button">
          <Plus size={18} className="mr-2" />ユーザー追加
        </Button>
      </div>

      {users.length === 0 ? (
        <Card className="card-surface"><CardContent className="py-12 text-center">
          <Users size={48} className="mx-auto mb-4 text-muted-foreground/50" />
          <p className="text-muted-foreground">ユーザーがいません</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-3">
          {users.map(u => (
            <Card key={u.id} className="card-surface hover:shadow-md transition-shadow" data-testid={`user-card-${u.id}`}>
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <UserCircle size={24} className="text-primary" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">{u.name}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full border ${ROLE_COLORS[u.role] || ROLE_COLORS.readonly}`}>
                          {ROLE_LABELS[u.role] || u.role}
                        </span>
                        {u.id === currentUser?.id && <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">自分</span>}
                      </div>
                      <p className="text-sm text-muted-foreground">{u.email}</p>
                      {isSystemAdmin && u.facility_id && u.facility_id !== 'None' && (
                        <p className="text-xs text-muted-foreground">
                          事業所: {facilities.find(f => f.id === u.facility_id)?.name || u.facility_id}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`}>
                      <Pencil size={14} className="mr-1" />編集
                    </Button>
                    {u.id !== currentUser?.id && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" variant="outline" className="text-destructive hover:text-destructive" data-testid={`delete-user-${u.id}`}>
                            <Trash2 size={14} />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>ユーザーを削除しますか？</AlertDialogTitle>
                            <AlertDialogDescription>{u.name} さんのアカウントを削除します。この操作は取り消せません。</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>キャンセル</AlertDialogCancel>
                            <AlertDialogAction onClick={() => handleDelete(u.id)}>削除</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle>{editingUser ? 'ユーザー編集' : '新規ユーザー追加'}</DialogTitle>
            <DialogDescription>{editingUser ? 'ロールを変更できます' : '新しいユーザーを事業所に追加します'}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>氏名 *</Label>
              <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} required data-testid="user-name-input" />
            </div>
            {!editingUser && (
              <>
                <div className="space-y-2">
                  <Label>メールアドレス *</Label>
                  <Input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} required data-testid="user-email-input" />
                </div>
                <div className="space-y-2">
                  <Label>パスワード * (6文字以上)</Label>
                  <Input type="password" value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))} required data-testid="user-password-input" />
                </div>
                {isSystemAdmin && facilities.length > 0 && (
                  <div className="space-y-2">
                    <Label>所属事業所</Label>
                    <Select value={form.facility_id} onValueChange={v => setForm(p => ({ ...p, facility_id: v }))}>
                      <SelectTrigger data-testid="user-facility-select"><SelectValue placeholder="事業所を選択" /></SelectTrigger>
                      <SelectContent>
                        {facilities.map(f => (
                          <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </>
            )}
            <div className="space-y-2">
              <Label>ロール</Label>
              <Select value={form.role} onValueChange={v => setForm(p => ({ ...p, role: v }))}>
                <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ASSIGNABLE_ROLES.map(r => (
                    <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>キャンセル</Button>
              <Button className="btn-active" onClick={handleSave} disabled={saving || !form.name} data-testid="user-save-button">
                {saving ? '保存中...' : (editingUser ? '更新' : '追加')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
